import type { MutableRefObject } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { type Payouts, type ElementTypes } from '@airwallex/components-sdk'

import { toast } from '@webapp/components/Notification'
import { useProcurifyIntl } from '@webapp/hooks'
import { createBeneficiaryUseCase } from '@webapp/platform/banking/business-logic/use-cases/CreateBeneficiary'
import { type PaymentMethodCategoryType } from '@webapp/platform/banking/entities/enums/PaymentMethodCategoryEnum'
import { useQueryAirwallexSdkInitialization } from '@webapp/platform/banking/hooks/useQueryAirwallexSdkInitialization'
import { useQueryTransfersAuthCode } from '@webapp/platform/banking/hooks/useQueryTransfersAuthCode'
import messages from './messages'

export interface IUseCreateBeneficiaryPresenterProps {
  paymentMethodCategory: Exclude<
    PaymentMethodCategoryType,
    'EXTERNAL' | 'INTERNATIONAL_US'
  >
  mountElementId: string
  onSubmit: (
    beneficiaryFormSubmitResponse: Payouts.BeneficiaryFormSubmitResponse
  ) => void
  onReady?: () => void
  onChange?: (event: Payouts.BeneficiaryFormElementEvent['change']) => void
  onError?: (error: Error) => void
}

export interface UseCreateBeneficiaryPresenterResult {
  isReady: boolean
  isSubmitting: boolean
  isInitializing: boolean
  beneficiaryFormRef: MutableRefObject<ElementTypes['beneficiaryForm'] | null>
  handleSubmit: () => Promise<void>
}

export const useCreateBeneficiaryPresenter = ({
  paymentMethodCategory,
  mountElementId,
  onSubmit,
  onReady,
  onChange,
  onError,
}: IUseCreateBeneficiaryPresenterProps): UseCreateBeneficiaryPresenterResult => {
  const intl = useProcurifyIntl()

  const [isReady, setIsReady] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const beneficiaryFormRef = useRef<ElementTypes['beneficiaryForm'] | null>(
    null
  )
  const isMountedRef = useRef(true)
  const hasInitializedRef = useRef(false)

  const { data: authData, isFetching: isFetchingAuthCode } =
    useQueryTransfersAuthCode({
      enabled: true,
      onError: (error) => {
        onError?.(error)
      },
    })

  const { initResult } = useQueryAirwallexSdkInitialization({
    authData: {
      authCode: authData?.authCode,
      codeVerifier: authData?.codeVerifier,
    },
  })

  const isAwxSdkReady = !!initResult

  const initializeBeneficiaryForm = useCallback(async () => {
    try {
      const element = await createBeneficiaryUseCase({
        mountElementId,
        paymentMethodCategory,
        onReady: () => {
          if (isMountedRef.current) {
            setIsReady(true)
            onReady?.()
          }
        },
        onChange: (event) => {
          onChange?.(event)
        },
        onError: (err) => {
          onError?.(err)
        },
      })

      if (isMountedRef.current) {
        beneficiaryFormRef.current = element
      } else {
        element?.unmount()
      }
    } catch (err) {
      const initError = err instanceof Error ? err : new Error('Unknown error')
      if (isMountedRef.current) {
        onError?.(initError)
      }
    }
  }, [mountElementId, paymentMethodCategory, onReady, onChange, onError])

  // Initialize beneficiary form once SDK is ready- only once
  useEffect(() => {
    if (isAwxSdkReady && !hasInitializedRef.current) {
      hasInitializedRef.current = true
      initializeBeneficiaryForm()
    }
  }, [isAwxSdkReady, initializeBeneficiaryForm])

  // Cleanup only on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (beneficiaryFormRef.current) {
        beneficiaryFormRef.current.unmount()
        beneficiaryFormRef.current = null
      }
    }
  }, [])

  const submitBeneficiaryForm =
    async (): Promise<Payouts.BeneficiaryFormSubmitResponse | null> => {
      if (!beneficiaryFormRef.current) {
        return null
      }
      return await beneficiaryFormRef.current.submit()
    }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const beneficiaryFormSubmitResponse = await submitBeneficiaryForm()

      if (beneficiaryFormSubmitResponse) {
        const { errors } = beneficiaryFormSubmitResponse

        if (errors && Object.keys(errors).length > 0) {
          toast.error({
            title: intl.formatMessage(messages.submissionErrorTitle),
            children: intl.formatMessage(messages.submissionErrorMessage),
          })

          return
        }

        onSubmit(beneficiaryFormSubmitResponse)
      }
    } catch (error) {
      const submitError =
        error instanceof Error
          ? error
          : new Error(intl.formatMessage(messages.submissionError))

      toast.error({
        title: intl.formatMessage(messages.errorToastTitle),
        children: submitError.message,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    isReady,
    isSubmitting,
    isInitializing: isFetchingAuthCode,
    beneficiaryFormRef,
    handleSubmit,
  }
}
